import type { KnowledgeArticleDTO } from '@conversed/rest/src/contracts/article-architect/article-architect.dto';
import {
  KnowledgeArticleSourceType,
  KnowledgeArticleStatus,
} from '@conversed/rest/src/contracts/article-architect/article-architect.enums';

export const SOURCE_BADGE_COLORS: Record<
  KnowledgeArticleSourceType,
  { bgColor: string; textColor: string }
> = {
  [KnowledgeArticleSourceType.TrainAI]: {
    bgColor: '#ede9fe',
    textColor: '#7c3aed',
  },
  [KnowledgeArticleSourceType.Upload]: {
    bgColor: '#dbeafe',
    textColor: '#2563eb',
  },
  [KnowledgeArticleSourceType.BCCEmail]: {
    bgColor: '#f0fdfa',
    textColor: '#0d9488',
  },
  [KnowledgeArticleSourceType.Topdesk]: {
    bgColor: '#fff7ed',
    textColor: '#ea580c',
  },
};

export const STATUS_BADGE_COLORS: Record<
  KnowledgeArticleStatus,
  { bgColor: string; textColor: string }
> = {
  [KnowledgeArticleStatus.Published]: {
    bgColor: '#dcfce7',
    textColor: '#166534',
  },
  [KnowledgeArticleStatus.AwaitingReview]: {
    bgColor: '#ffedd5',
    textColor: '#9a3412',
  },
  [KnowledgeArticleStatus.Draft]: {
    bgColor: '#f3f4f6',
    textColor: '#374151',
  },
};

// Palette of 12 pastel color pairs that all look good on a white background.
// Colors are assigned deterministically by hashing the category name so the
// same category always gets the same color without any DB storage.
const CATEGORY_COLOR_PALETTE: { bgColor: string; textColor: string }[] = [
  { bgColor: '#fdf2f8', textColor: '#be185d' }, // pink-50
  { bgColor: '#faf5ff', textColor: '#7c3aed' }, // purple-50
  { bgColor: '#eff6ff', textColor: '#1d4ed8' }, // blue-50
  { bgColor: '#f0f9ff', textColor: '#0369a1' }, // sky-50
  { bgColor: '#f0fdfa', textColor: '#0f766e' }, // teal-50
  { bgColor: '#f0fdf4', textColor: '#15803d' }, // green-50
  { bgColor: '#fefce8', textColor: '#a16207' }, // yellow-50
  { bgColor: '#fff7ed', textColor: '#c2410c' }, // orange-50
  { bgColor: '#fef2f2', textColor: '#b91c1c' }, // red-50
  { bgColor: '#f5f3ff', textColor: '#6d28d9' }, // violet-50
  { bgColor: '#ecfdf5', textColor: '#065f46' }, // emerald-50
  { bgColor: '#ecfeff', textColor: '#075985' }, // cyan-50
];

const CATEGORY_FALLBACK_COLORS = { bgColor: '#f3f4f6', textColor: '#374151' };

export const getCategoryBadgeColors = (
  category: string,
): { bgColor: string; textColor: string } => {
  if (!category) {
    return CATEGORY_FALLBACK_COLORS;
  }
  // Sum char codes and mod by palette length to get a stable, deterministic index
  const charCodeSum = category
    .split('')
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return (
    CATEGORY_COLOR_PALETTE[charCodeSum % CATEGORY_COLOR_PALETTE.length] ??
    CATEGORY_FALLBACK_COLORS
  );
};

// Returns all three badge color sets for an article in one call, avoiding
// repeated lookups across components that need source, status, and category colors.
export const getArticleBadgeColors = (article: KnowledgeArticleDTO) => {
  const sourceColors =
    SOURCE_BADGE_COLORS[article.sourceType as KnowledgeArticleSourceType];
  const statusColors =
    STATUS_BADGE_COLORS[article.status as KnowledgeArticleStatus] ??
    STATUS_BADGE_COLORS[KnowledgeArticleStatus.Draft];
  const categoryColors = getCategoryBadgeColors(article.category ?? '');
  return { sourceColors, statusColors, categoryColors };
};
